import { Dialog, DialogContent } from '../ui/dialog'
import { DialogTrigger } from '@radix-ui/react-dialog'
import { PDFViewer } from '@react-pdf/renderer'
import DataTablePDF from '../report_pdf/datatable_export'
import { Button } from '../ui/button'
import { Row } from '@tanstack/react-table'
import { Download, FileSpreadsheet, Table } from 'lucide-react'
import { mkConfig, generateCsv, download } from 'export-to-csv'
import { format } from 'date-fns'
import { dateformat } from '@/utils/GlobalConstants'
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu'
import { WizardContext } from '../wizard/wizardProvider'
import React from 'react'

interface GeneratePDFProps {
    title: string,
    header_colour: string,
    rows: Row<any>[],
    alternate_colours?: boolean
}

const ExportTable = ({ title, header_colour, rows, alternate_colours }: GeneratePDFProps) => {
    const wizard = React.useContext(WizardContext);
    const [open, setOpen] = React.useState(false);

    React.useEffect(() => {
        if (wizard.showWizardPointer && wizard.currentStep?.sendClick && wizard.currentStep.stepComponentId === "exportButton") {
            let timeoutId = setTimeout(() => setOpen(true), 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [wizard.showWizardPointer, wizard.currentStep]);

    function exportToCSV() {
        exportExcel(rows, title || 'export');
    }

    function getHexColor(color: string) {
        switch (color) {
            case "bg-table-risks":
                return "#d97706"
            case "bg-table-support":
                return "#ffd001"
            case "bg-table-issues":
                return "#ef4444"
            case "bg-table-decisions":
                return "#22c55e"
            case "bg-table-actions":
                return "#2463eb"
            default:
                return "#2463eb"
        }
    }

    const csvConfig = (table_title: string) => mkConfig({
        fieldSeparator: ',',
        filename: `${table_title}-${format(new Date(), dateformat)} `, // export file name (without .csv)
        decimalSeparator: '.',
        useKeysAsHeaders: true,
    })

    // export function
    // Note: change _ in Row<_>[] with your Typescript type.
    const exportExcel = (rows: Row<any>[], table_title: string) => {
        const rowData = rows.map((row) => {
            const original = { ...row.original }
            for (const key in original) {
                if (original[key] instanceof Date) {
                    original[key] = format((original[key] as Date), dateformat)
                }
            }
            return original
        })
        const csv = generateCsv(csvConfig(table_title))(rowData)
        download(csvConfig(table_title))(csv)
    }

    return (
        <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="outline"
                    id="exportButton"
                    className="ml-2 hidden h-8 lg:flex mt-1"
                >
                    <Download />
                    Export
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-min">
                <DropdownMenuLabel>Export to</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <Button
                    variant="ghost"
                    onClick={() => exportToCSV()}
                    className='w-full justify-between'
                >
                    <Table /><span>CSV</span>
                </Button>
                <Dialog>
                    <DialogTrigger className='w-full'>
                        <Button variant="ghost" className='w-full justify-between'>
                            <FileSpreadsheet /><span>PDF</span>
                        </Button>
                    </DialogTrigger>
                    <DialogContent className='min-w-full h-full'>
                        <PDFViewer style={{ width: "100%", height: "100%" }}>
                            <DataTablePDF title={title} header_colour={getHexColor(header_colour)} rows={rows} alternate_colours={alternate_colours} />
                        </PDFViewer>
                    </DialogContent>
                </Dialog>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export default ExportTable